<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header">
        <span class="hris-page-eyebrow">Employee Management</span>
        <h1 class="hris-page-title">Set Employee Permissions</h1>
        <p class="hris-page-subtitle">Search for an employee and configure their module access.</p>
      </div>

      <div class="hris-toolbar">
        <q-form ref="vForm" @submit="onSubmitSearch" class="full-width">
          <q-input
            v-model="searchModel"
            dense
            outlined
            color="green"
            :rules="[formRules.required]"
            placeholder="Search employee here..."
          >
            <template v-slot:append>
              <q-icon name="fa fa-search" />
            </template>
          </q-input>
        </q-form>
      </div>

      <div class="hris-panel">
        <h3 class="hris-panel-title">Search Result</h3>
        <q-table
          flat
          :data="searchResultData"
          :columns="searchResultColumns"
          row-key="id"
          :hide-bottom="searchResultData.length > 0"
          :loading="loading"
        >
          <template v-slot:loading>
            <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
              <q-spinner color="green" size="40px"></q-spinner>
              <div>Searching employees...</div>
            </div>
          </template>
          <q-tr slot="body" slot-scope="props" :props="props" class="cursor-pointer"
            @click="searchRowClick(props.row.name, props.row.employee_id)">
            <q-td key="employee_id" :props="props">{{ props.row.employee_id }}</q-td>
            <q-td key="name" :props="props">{{ props.row.name }}</q-td>
            <q-td key="position" :props="props">{{ props.row.position }}</q-td>
            <q-td key="department" :props="props">{{ props.row.department }}</q-td>
          </q-tr>
          <template v-slot:no-data="{ message }">
            <div class="hris-empty full-width">{{ message }}</div>
          </template>
        </q-table>
      </div>

      <div v-if="modulesArea" class="hris-panel q-mt-md">
        <div class="row items-center q-mb-md" style="gap: 12px;">
          <h3 class="hris-panel-title" style="margin-bottom: 0;">Module Permissions</h3>
          <span class="hris-badge hris-badge-neutral">{{ empName }}</span>
        </div>
        <div class="hris-grid">
          <div v-for="mods in modules" :key="mods.id" class="hris-content-card">
            <div class="hris-content-card-body">
              <q-item tag="label" class="q-px-none">
                <q-item-section side top>
                  <q-toggle
                    v-model="mods.enabled"
                    @input="setVal(mods)"
                    checked-icon="fa fa-check"
                    color="green"
                    unchecked-icon="fa fa-times"
                  ></q-toggle>
                </q-item-section>
                <q-item-section>
                  <q-item-label class="qitemLabel">{{ humanize(mods.name) }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-tooltip
                anchor="top middle"
                self="bottom middle"
                :offset="[10, 10]"
                transition-show="scale"
                transition-hide="scale"
                :content-class="mods.enabled ? 'bg-red' : 'bg-green'"
                content-style="font-size: 13px"
              >
                <div v-if="mods.enabled === true">Click to disable module.</div>
                <div v-else>Click to enable module.</div>
              </q-tooltip>
              <div v-if="mods.sub_modules.length > 0" class="q-mt-sm">
                <q-expansion-item caption="Sub-menus" dense>
                  <div v-for="submods in mods.sub_modules" :key="submods.id" class="q-pl-sm">
                    <q-item tag="label" class="q-px-none">
                      <q-item-section side top>
                        <q-toggle
                          v-model="submods.enabled"
                          @input="setVal(submods)"
                          checked-icon="fa fa-check"
                          color="green"
                          unchecked-icon="fa fa-times"
                        ></q-toggle>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label class="qitemLabel">{{ humanize(submods.name) }}</q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-tooltip
                      anchor="top middle"
                      self="bottom middle"
                      :offset="[10, 10]"
                      transition-show="scale"
                      transition-hide="scale"
                      :content-class="submods.enabled ? 'bg-red' : 'bg-green'"
                      content-style="font-size: 13px"
                    >
                      <div v-if="submods.enabled === true">Click to disable module.</div>
                      <div v-else>Click to enable module.</div>
                    </q-tooltip>
                  </div>
                </q-expansion-item>
              </div>
            </div>
          </div>
        </div>
        <div v-if="confirmConfig" class="hris-form-actions q-mt-md">
          <q-btn
            unelevated
            class="hris-q-primary"
            :loading="modEffectLoading"
            @click="moduleStatus"
            label="Confirm"
            style="min-width: 180px;"
          >
            <template v-slot:loading>
              <q-spinner-gears class="on-left" />
              Updating...
            </template>
          </q-btn>
        </div>
      </div>

      <q-dialog v-model="loadingModel" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal" style="max-width: 320px;">
          <div class="hris-modal-body">
            <div class="hris-loading">
              <q-spinner color="green" size="40px" />
              <span>Fetching records...</span>
            </div>
          </div>
        </div>
      </q-dialog>
    </div>
  </div>
</template><script>
import axios from 'axios';

const scrollToTop = () => {
  const c = document.documentElement.scrollTop || document.body.scrollTop;
  if (c > 0) {
    window.requestAnimationFrame(scrollToTop);
    window.scrollTo(0, c - c / 8);
  }
};

export default {
  name: 'ModulePermissions',
  data() {
    return {
      empName: '',
      loadingModel: false,
      loading: false,
      modEffectLoading: false,
      modulesArea: false,
      confirmConfig: false,
      searchModel: null,
      searchResultData: [],
      searchResultColumns: [{
        name: 'employee_id',
        field: 'employee_id',
        label: 'Employee ID',
        style: 'width: 200px',
        align: 'center',
        headerStyle: 'width: 200px'
      },
      {
        name: 'name',
        field: 'name',
        label: 'Name',
        style: 'width: 300px; padding-left: 100px',
        align: 'left',
        headerStyle: 'width: 300px; text-align:center'
      },
      {
        name: 'position',
        field: 'position',
        label: 'Position',
        style: 'width: 300px; padding-left: 100px',
        align: 'left',
        headerStyle: 'width: 300px; text-align:center'
      },
      {
        name: 'department',
        field: 'department',
        label: 'Department',
        align: 'center',
      }
      ],
      modules: [
        {
          "name": "Timekeeping and Forms",
          "enabled": true,
          "parent_module_id": 0,
          "iconClass": "fa-clock",
          "sub_modules": [
            {
              "name": "Attendance",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/attendance",
              "iconClass": "fa-user"
            },
            {
              "name": "Calendar",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/calendar",
              "iconClass": "fa-calendar"
            },
            {
              "name": "Change of Work Schedule",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/change-schedule",
              "iconClass": "fa-calendar-times"
            },
            {
              "name": "Half Day",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/halfday",
              "iconClass": "fa-history"
            },
            {
              "name": "Official Business Trip",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/business-trip",
              "iconClass": "fa-plane"
            },
            {
              "name": "Overtime",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/overtime",
              "iconClass": "fa-stopwatch"
            },
            {
              "name": "Undertime",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/undertime",
              "iconClass": "fa-history"
            },
            {
              "name": "Work From Home",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/wfh",
              "iconClass": "fa-laptop-house"
            },
            {
              "name": "Form 2316",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/form-2316",
              "iconClass": "fa-table"
            }
          ]
        },

        {
          "name": "Training",
          "enabled": true,
          "parent_module_id": 0,
          "iconClass": "fa-graduation-cap",
          "sub_modules": [
            {
              "name": "E-Learning",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/lessons",
              "iconClass": "fa-book-open"
            }
          ]
        },

        {
          "name": "Leaves",
          "enabled": true,
          "parent_module_id": 0,
          "iconClass": "fa-person-walking-arrow-right",
          "sub_modules": [
            {
              "name": "My Applications",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/my-leave-applications",
              "iconClass": "fa-copy"
            },
            {
              "name": "Application Form",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/application-form",
              "iconClass": "fa-file-alt"
            },
            {
              "name": "My Leave Credits",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/my-leave-credits",
              "iconClass": "fa-list-ol"
            },
            {
              "name": "My Leave History",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/employee-leave-history",
              "iconClass": "fa-history"
            }
          ]
        },

        {
          "name": "Government Reports",
          "enabled": true,
          "parent_module_id": 0,
          "iconClass": "fa-code",
          "sub_modules": [
            {
              "name": "SSS Employee Contribution",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/sss-employee-contribution",
              "iconClass": "fa-list-alt"
            },
            {
              "name": "Philhealth Employee Contribution",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/philhealth-employee-contribution",
              "iconClass": "fa-list-alt"
            },
            {
              "name": "Pag-ibig Employee Contribution",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/pagibig-employee-contribution",
              "iconClass": "fa-list-alt"
            },
            {
              "name": "Alphalist",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/alphalist",
              "iconClass": "fa-list-alt"
            }
          ]
        },

        {
          "name": "Announcement",
          "enabled": true,
          "parent_module_id": 0,
          "iconClass": "fa-bullhorn",
          "sub_modules": [
            {
              "name": "List of Announcements",
              "enabled": true,
              "parent_module_id": 0,
              "route": "/announcement-list",
              "iconClass": "fa-bullhorn"
            }
          ]
        },
      ],
      updatedMods: [],
      selectedIsAdmin: false,
      selectedEmployeeId: 0,
      borderRadius: 'border-radius'
    }
  },

  methods: {
    onSubmitSearch: function () {
      this.loading = true;
      this.modulesArea = false;
      this.$refs.vForm.validate();

      axios.create({
        baseURL: this.apiUrl,
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      })
        .get('/employees/search', {
          params: {
            keyword: this.searchModel
          }
        })
        .then((res) => {
          this.loading = false;
          this.searchResultData = [];
          if (res.data.data.length > 0) {
            for (let emp of res.data.data) {
              this.searchResultData.push({
                id: emp.id,
                name: this.toTitleCase(emp.name),
                employee_id: emp.employee_id,
                company_id: emp.company_id,
                position: emp.position,
                department: emp.department
              });
            }
          } else {
            this.loadingModel = false;
            this.modulesArea = false;
            this.confirmConfig = false;
          }
        })
    },

    searchRowClick: function (rowName, rowID) {
      this.empName = rowName;
      this.selectedEmployeeId = rowID;
      this.setAllEnabled();
      this.loadingModel = true;

      let formData = new FormData();
      formData.append("id", this.selectedEmployeeId);
      axios.create({
        baseURL: this.apiUrl,
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      })
        .post(`/employees/get/modules`, formData)
        .then((res) => {
          this.loadingModel = false;
          this.modulesArea = true;
          this.confirmConfig = true;
          if (res.data.rawData) {
            this.modules = res.data.rawData;
          } else if (res.data.data) {
            this.modules = res.data.data;
          }
        });
    },

    setVal: function (value) {
      var i, v, a;
      if (value.name == "admin") {
        for (i = 0; i < this.modules.length; i++) {
          if (value.enabled == false) {
            this.modules[i].enabled = false;
            if (this.modules[i].sub_modules.length > 0) {
              for (v = 0; v < this.modules[i].sub_modules.length; v++) {
                this.modules[i].sub_modules[v].enabled = false;
              }
            }
          } else {
            this.modules[i].enabled = true;
            if (this.modules[i].sub_modules.length > 0) {
              for (v = 0; v < this.modules[i].sub_modules.length; v++) {
                this.modules[i].sub_modules[v].enabled = true;
              }
            }
          }
        }
      } else if (value.name != "admin" && this.selectedIsAdmin == true) {
        this.modules[0].enabled = false;
        for (i = 1; i < this.modules.length; i++) {
          if (this.modules[i].enabled == false && this.modules[i].sub_modules.length > 0) {
            for (v = 0; v < this.modules[i].sub_modules.length; v++) {
              this.modules[i].sub_modules[v].enabled = false;
            }
          }
        }
      } else {
        for (i = 1; i < this.modules.length; i++) {
          if (this.modules[i].enabled == false && this.modules[i].sub_modules.length > 0) {
            for (v = 0; v < this.modules[i].sub_modules.length; v++) {
              this.modules[i].sub_modules[v].enabled = false;
            }
          }
        }
      }
    },

    moduleStatus: function () {
      this.modEffectLoading = true;
      this.updatedMods = [];
      for (let updateMods of this.modules) {
        let subMods = [];
        if (updateMods.sub_modules) {
          for (let updateSubMods of updateMods.sub_modules) {
            subMods.push({
              name: updateSubMods.name,
              iconClass: updateSubMods.iconClass,
              route: updateSubMods.route,
              enabled: updateSubMods.enabled
            });
          }
        }

        this.updatedMods.push({
          name: updateMods.name,
          enabled: updateMods.enabled,
          sub: subMods.length > 0 ? subMods : undefined // Only add 'sub' if it has elements
        });
      }

      let formData = new FormData();

      formData.append("employee_id", this.selectedEmployeeId);
      formData.append("modules", JSON.stringify(this.updatedMods));
      formData.append("raw_module_data", JSON.stringify(this.modules));


      axios.create({
        baseURL: this.apiUrl,
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      }).post('/employees/change/module', formData)

      setTimeout(() => {
        this.modEffectLoading = false
        this.$q.notify({
          color: 'blue-9',
          textColor: 'white',
          icon: 'fa fa-cogs',
          message: 'Modules Set Success'
        });
        scrollToTop();
      }, 1500);
    },

    setAllEnabled: function () {
      for (let mod of this.modules) {
        mod.enabled = true;
        for (let sub of mod.sub_modules) {
          sub.enabled = true;
        }
      }
    }
  }
}
</script>
<style lang="css" scoped>
.qitemLabel {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -o-user-select: none;
  user-select: none;
}

.semi-round {
  border-radius: 10px;
}
</style>
